"use client";

import type { SupDicApyPeriod } from "@/components/Ui/Label/Apy";
import { RewardsSettings } from "@/hooks/data/useRewardSettings";
import type { SupSingleVaultSimplified, SupSingleVaultV3, SupVaultReward, SupWeb3TokenSymbol } from "@/types";
import React, { useMemo } from "react";

import dappConfig from "@/../config/dapp.config";
import dictionary from "@/../config/dictionary.config";
import Image from "next/image";

import { SupCoinAlt, SupTransfer } from "@repo/core-ui";
import { SupText } from "@repo/core-ui";

import useVaults from "@/hooks/api/useVaults";
import { formatProtocolName } from "@/utils";
import { twMerge } from "tailwind-merge";

export function mapAssetIcon(item: { underlying_image: string; underlying_symbol: SupWeb3TokenSymbol }, size: number = 20) {
  if (item.underlying_image || dappConfig.localTokens.includes(item.underlying_symbol)) {
    const imageSrc = item.underlying_symbol !== "MIM" && item.underlying_image ? item.underlying_image : `/assets/img/tokens/${item.underlying_symbol}.webp`;
    return <img width={size} height={size} src={imageSrc} alt={item.underlying_symbol} className={`object-cover h-[${size}px] w-auto rounded-full object-center`} />;
  }
  return <SupCoinAlt className={`h-[${size}px] w-auto rounded-full stroke-0 object-cover object-center`} />;
}

export function mapProtocolIcon(
  item: {
    image_url: string | undefined;
    protocol: { name: string } | string;
  },
  showLabel: boolean = false,
  noBorder = false,
  className: string = "",
  imgClassName: string = ""
) {
  const img = item.image_url || dictionary.protocol.noProtocolImage;
  return (
    <div className={twMerge("sup-badge-protocol", noBorder && "border-none", className)}>
      <small className={twMerge(!showLabel && "hidden")}>Protocol</small>
      <Image unoptimized={true} width="20" height="20" src={img} className={imgClassName} alt="" />
      <span>{formatProtocolName(typeof item.protocol === "string" ? item.protocol : item.protocol.name)}</span>
    </div>
  );
}

export function mapVaultIcon(
  item: {
    underlying_symbol: SupWeb3TokenSymbol;
    underlying_image: string;
  },
  showLabel: boolean = false,
  label: string = "Vault",
  iconSize: number = 12,
  customCssClass: string | undefined = undefined
) {
  return (
    <div className={twMerge(customCssClass && customCssClass, "sup-badge-vault")}>
      <small className={twMerge(!showLabel && "hidden")}>{label}</small>
      {mapAssetIcon(item, iconSize)}
      <span>{item.underlying_symbol}</span>
    </div>
  );
}

export function mapAddressWithSuperScan(address: string, onClick: () => void, short = false) {
  return (
    <div className="linkstyle group inline-flex cursor-pointer items-center whitespace-nowrap font-light text-neutral-900 decoration-from-font lg:font-normal" onClick={onClick}>
      <SupText variant="subtitle2">Track on Activity Page:&nbsp;</SupText>
      <code className="select-none flex-row whitespace-nowrap rounded-lg bg-[transparent] p-0 font-mono text-sm tracking-tighter md:group-hover:text-neutral-300 lg:text-xs">
        <span>{address.substring(0, 7)}</span>
        {!short && <span className="hidden lg:inline-flex">{address.slice(7, address.length - 6)}</span>}
        <span className={twMerge(!short && "inline-flex lg:hidden")}>...</span>
        <span>{address.slice(-5)}</span>
      </code>
      <SupTransfer className="ml-1.5 h-4 md:opacity-25 md:group-hover:text-neutral-300" />
    </div>
  );
}

export function useFilterSortVaults(vaults: { slug: string }[] | undefined, favorites: string[], favoritesOnly: boolean) {
  if (!vaults) {
    return [] as SupSingleVaultV3[];
  }
  if (favorites.length > 0 && favoritesOnly) {
    return vaults.filter(vault => favorites.includes(vault.slug));
  }
  return vaults;
}

export function useGetSingleVault(vaultId: number | string, useSlug: boolean = false) {
  const { data } = useVaults();
  return useMemo(() => {
    return data?.find(vault => (useSlug ? vault.slug === vaultId : vault.id === vaultId));
  }, [data, useSlug, vaultId]);
}

export function shortenString(address: string, front: number, back: number) {
  if (address) {
    return address.substring(0, front) + "..." + address.substring(address.length - back);
  }
  return null;
}

export function supHexToHsl(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (!Array.isArray(result) || result.length <= 2) return;

  let r = parseInt(result[1], 16);
  let g = parseInt(result[2], 16);
  let b = parseInt(result[3], 16);

  // noinspection CommaExpressionJS
  (r /= 255), (g /= 255), (b /= 255);
  let max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;
  let _l;

  if (max == min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    // @ts-ignore
    h /= 6;
  }

  s = s * 100;
  s = Math.round(s);
  l = l * 100;
  _l = l >= 20 ? l - 20 : 0;
  l = Math.round(l);
  // @ts-ignore
  h = Math.round(360 * h);

  const colorBefore = "hsl(" + h + ", " + s + "%, " + _l + "%)";
  const colorAfter = "hsl(" + h + ", " + s + "%, " + l + "%)";
  const textColor = l >= 60 ? "#404040" : "#fff";
  const activeColor = "hsl(" + h + ", " + s + "%, " + l + 4 + "%)";

  return { colorBefore, colorAfter, textColor, activeColor };
}

export function calculateAge(time: string) {
  const date = new Date(time);
  const current = new Date();
  const difference = current.getTime() - date.getTime();
  return Math.ceil(difference / (1000 * 3600 * 24)).toString();
}

export function getProtocolName(vault: SupSingleVaultSimplified) {
  return vault.protocol ? vault.protocol.name : dictionary.protocol.noProtocolName;
}

export function getProtocolImage(vault: { protocol: { graphics: { icon: string; avatar: string }; is_authenticated: boolean } } | SupSingleVaultV3) {
  return vault.protocol?.is_authenticated
    ? vault.protocol.graphics.icon || vault.protocol.graphics.avatar || dictionary.protocol.noProtocolImage
    : dictionary.protocol.noProtocolImage;
}

export function getVaultPublicUrl(vault: SupSingleVaultSimplified) {
  return `https://app.superform.xyz/vault/${vault.slug}`;
}

export function getVaultDecimals(vault: SupSingleVaultV3) {
  return vault.decimals;
}

export function getVaultApy(vault: SupSingleVaultSimplified, period: SupDicApyPeriod = "month", useFallback = true): number {
  let apy;
  switch (period) {
    case "day":
      apy = vault.vault_statistics?.apy_day ?? dictionary.statsDefaultValue;
      break;
    case "week":
      apy = vault.vault_statistics?.apy_week ?? dictionary.statsDefaultValue;
      break;
    case "month":
    default:
      apy = vault.vault_statistics?.apy_month ?? dictionary.statsDefaultValue;
      break;
  }
  return apy !== dictionary.statsDefaultValue ? apy : useFallback ? vault.vault_statistics.displayApy : dictionary.statsDefaultValue;
}

export function getNetVaultApy(
  vault: SupSingleVaultSimplified,
  options: RewardsSettings | undefined = undefined,
  period: SupDicApyPeriod | undefined = "month",
  useFallback: boolean = false
): number {
  const baseApy = getVaultApy(vault, period, useFallback);
  if (!vault.rewards?.length || !options?.includeRewardsInApy || baseApy === dictionary.statsDefaultValue) {
    return baseApy;
  }

  const preparedRewards = options.rewardsOverloadFunc?.(vault.rewards) ?? vault.rewards;
  const rewardsApy = preparedRewards.reduce((acc, reward) => acc - (reward.type === "token" && reward.rate_type === "apy" ? reward.reward_rate : 0) * -1, 0);
  return baseApy + rewardsApy;
}
export function enrichWithRewardsApy(
  vault: { rewards: SupVaultReward[] },
  value: number,
  options:
    | {
        includeRewardsInApy?: boolean;
        rewardsOverloadFunc?: (reward: SupVaultReward[]) => SupVaultReward[];
      }
    | undefined = undefined
): number {
  if (!vault.rewards?.length || !options?.includeRewardsInApy) {
    return value;
  }

  const preparedRewards = options.rewardsOverloadFunc?.(vault.rewards) ?? vault.rewards;
  const rewardsApy = preparedRewards.reduce((acc, reward) => acc - (reward.type === "token" && reward.rate_type === "apy" ? reward.reward_rate : 0) * -1, 0);
  return value + rewardsApy;
}

export function getVaultTvl(vault: SupSingleVaultSimplified): number {
  return vault.vault_statistics.displayTvl;
}

export function getVaultSharpe(vault: SupSingleVaultSimplified): number {
  return vault.vault_statistics.displaySharpe;
}

/**
 * Returns a hex color with alpha channel calculated from opacity param
 * @param {string} color
 * @param {number} opacity
 * @returns {string}
 */
export function hexColorWithAlpha(color: string, opacity: number): string {
  return `${color}${Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255)
    .toString(16)
    .toUpperCase()}`;
}

export function arrayIntersect<S>(a: S[], b: S[]) {
  const setA = new Set(a);
  const setB = new Set(b);
  return Array.from(new Set([...setA].filter(x => setB.has(x))));
}

export function escapeDescription(description: string) {
  return description.replace(/(\r\n|\n|\r)/gm, "&#013;").replace(/("|'|`|<|>“|”|‘|’)/gm, "&quot;");
}

export function unescapeDescription(description: string | undefined, includeLineBreaks: boolean = true, excludeQuotes: boolean = false) {
  const partialResult = (description ?? "").replace(/&quot;/gm, excludeQuotes ? " " : '"');
  return includeLineBreaks ? partialResult.replace(/&#013;/gm, "\n") : partialResult;
}
